import * as React from 'react';
import { Store } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { SelectedProduct } from '../../stores/campaign-wizard.store';

// ============ Types ============

interface NotificationPreviewProps {
  shopName: string;
  shopLogo?: string;
  title?: string;
  message: string;
  image?: string; // Main image (product or shop)
  products?: SelectedProduct[]; // For multi-products
  className?: string;
}

// ============ Component ============

export function NotificationPreview({
  shopName,
  shopLogo,
  title,
  message,
  image,
  products,
  className,
}: NotificationPreviewProps) {
  // Determine the preview image - use product image if available
  const previewImage = React.useMemo(() => {
    if (image) return image;
    if (products && products.length > 0 && products[0].image) {
      return products[0].image;
    }
    return null;
  }, [image, products]);

  return (
    <div className={cn('w-full max-w-[280px] mx-auto', className)}>
      {/* Simple phone frame */}
      <div className="relative bg-gray-900 rounded-[32px] p-2.5 shadow-xl">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-b-2xl z-10" />

        {/* Screen */}
        <div className="relative bg-white dark:bg-gray-800 rounded-[26px] overflow-hidden">
          {/* Status bar */}
          <div className="h-8 bg-white dark:bg-gray-800 flex items-center justify-center">
            <span className="text-[11px] font-medium text-gray-900 dark:text-gray-100">
              9:41
            </span>
          </div>

          {/* Lock screen background */}
          <div className="min-h-[340px] bg-gradient-to-b from-blue-400 to-purple-500 p-4">
            {/* Lock screen clock */}
            <div className="text-center text-white mt-4 mb-8">
              <p className="text-5xl font-light">9:41</p>
              <p className="text-sm mt-1">Lundi 13 avril</p>
            </div>

            {/* Notification card */}
            <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-2xl p-3 shadow-lg">
              {/* Header with shop logo */}
              <div className="flex items-center gap-2">
                {shopLogo ? (
                  <img
                    src={shopLogo}
                    alt={shopName}
                    className="size-8 rounded-lg object-cover shrink-0"
                  />
                ) : (
                  <div className="size-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                    <Store className="size-4 text-primary" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                    {shopName || 'Ma Boutique'}
                  </p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">
                    maintenant
                  </p>
                </div>
              </div>

              {/* Title & Message */}
              <div className="mt-2">
                {title && (
                  <p className="font-semibold text-sm text-gray-900 dark:text-gray-100 line-clamp-1">
                    {title}
                  </p>
                )}
                <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mt-0.5">
                  {message || 'Votre message apparaitra ici...'}
                </p>
              </div>

              {/* Single image preview */}
              {previewImage && (!products || products.length <= 1) && (
                <div className="mt-2 rounded-lg overflow-hidden">
                  <img
                    src={previewImage}
                    alt=""
                    className="w-full h-28 object-cover"
                  />
                </div>
              )}

              {/* Multi-product grid */}
              {products && products.length > 1 && (
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  {products.slice(0, 4).map((product) => (
                    <div
                      key={product.refId}
                      className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700"
                    >
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-14 object-cover"
                        />
                      ) : (
                        <div className="w-full h-14 bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                          <Store className="size-4 text-gray-400" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Home indicator */}
          <div className="h-6 bg-white dark:bg-gray-800 flex items-center justify-center">
            <div className="w-24 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}
